import * as React from 'react';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {ViewExploreIcon} from 'shells/icon/svg/view-explore-icon';
import {Stack} from 'shells/layout/stack/stack';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';
import {Body} from 'shells/typography/body';

import type {SavedFiltersReportType} from 'nutshell-graphql-types';
import type {GetRecentlySavedReports_recentlySavedReports as ReportView} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {SHARE_TYPE_JUST_MINE} from '../../base/saved-filter-modal/saved-filter-modal';

import {convertReportTypeGraphQlEnumToReportKey, getReportIcon} from '../report-constants';
import {SavedReportsModal} from '../saved-reports-modal';

import {ReportLoadingCard} from './report-loading-card';

import styles from './report-card-stack.module.css';

type Props = {
    reports: ReportView[];
    variant: 'viewed-reports' | 'saved-reports';
    isLoading: boolean;
    shouldShowEmptyState: boolean;
};

const getReportIconWithBackground = (reportType: SavedFiltersReportType): React.ReactElement => {
    const reportKey = convertReportTypeGraphQlEnumToReportKey(reportType);
    const Icon = getReportIcon(reportKey);

    return (
        <div className={styles['report-icon-container']}>
            <Icon color={colors.blue} size={24} />
        </div>
    );
};

export function ReportCardStack({
    reports,
    variant,
    isLoading,
    shouldShowEmptyState,
}: Props): React.ReactElement | null {
    const [isSavedReportsModalOpen, setIsSavedReportsModalOpen] = React.useState<boolean>(false);

    const title = variant === 'viewed-reports' ? 'Recently viewed' : 'Recently saved';
    const timestampLabel = variant === 'viewed-reports' ? 'Viewed' : 'Saved';
    const emptyStateText =
        variant === 'viewed-reports'
            ? 'Your recently viewed reports will show up here'
            : 'Your saved reports will show up here';

    if (isLoading) {
        return (
            <div className='mt-16 min-width-0'>
                <Stack spacing={12}>
                    <Body isBold={true} color='dark'>
                        {title}
                    </Body>
                    <ReportLoadingCard />
                    <ReportLoadingCard />
                    <ReportLoadingCard />
                </Stack>
            </div>
        );
    }

    if (shouldShowEmptyState) {
        return (
            <div className='mt-16'>
                <Body isBold={true} color='dark'>
                    {title}
                </Body>
                <div className='flex flex-dir-col align-center justify-center full-width full-height'>
                    <ViewExploreIcon size={40} />
                    <Body>{emptyStateText}</Body>
                </div>
            </div>
        );
    }

    if (!isLoading && (!reports || reports.length === 0)) {
        return null;
    }

    return (
        <div className='mt-16 min-width-0'>
            <Stack spacing={12}>
                <Stack isHorizontal={true} justifyContent='space-between'>
                    <Body isBold={true} color='dark'>
                        {title}
                    </Body>
                    {variant === 'saved-reports' && reports.length >= 3 && (
                        <Button
                            size='big'
                            variant='text-primary'
                            noPadding={true}
                            onClick={() => setIsSavedReportsModalOpen(true)}
                        >
                            View all
                        </Button>
                    )}
                </Stack>
                <SavedReportsModal
                    isOpen={isSavedReportsModalOpen}
                    onClose={() => setIsSavedReportsModalOpen(false)}
                    preselectedTab={SHARE_TYPE_JUST_MINE}
                />
                {reports.map((report) => (
                    <div
                        key={`${report.htmlUrlPath}-${report.displayTime}`}
                        onClick={() => appHistory.push(report.htmlUrlPath)}
                        className={styles['report-card-wrapper']}
                    >
                        <Card hasBorder={true} hasShadow={true} borderRadius={8}>
                            <div className={styles['card-content']}>
                                {getReportIconWithBackground(report.reportType)}
                                <div className='flex flex-dir-col gap-4 min-width-0 overflow-hidden'>
                                    <Body
                                        truncate={{type: 'block'}}
                                        color='dark'
                                        isBold={true}
                                        title={report.title}
                                    >
                                        {report.title}
                                    </Body>
                                    <Body
                                        truncate={{type: 'block'}}
                                        size='small'
                                        title={report.subtitle}
                                    >
                                        {report.subtitle}
                                    </Body>
                                    <Body size='small' color='light'>
                                        <div className='nowrap flex'>
                                            <span>{timestampLabel}&nbsp;</span>
                                            <RelativeTimestamp datetime={report.displayTime} />
                                        </div>
                                    </Body>
                                </div>
                            </div>
                        </Card>
                    </div>
                ))}
            </Stack>
        </div>
    );
}
